/**
 * Navigation retour intelligente : quand on quitte une "page liste" (Pilotage,
 * Prospects, Dashboard, Fil, Wall, Tâches…) pour ouvrir une fiche prospect, on
 * sauvegarde le chemin actuel + la position de scroll dans sessionStorage.
 * Au retour depuis la fiche prospect, on restaure exactement l'URL + le scroll.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const KEY = 'valnexo_return_state';

const _save = (path, search, scroll) => {
    try {
        sessionStorage.setItem(KEY, JSON.stringify({
            path: (path || '/prospects') + (search || ''),
            scroll: scroll || 0,
            at: Date.now(),
        }));
    } catch { /* quota / private mode */ }
};

const _read = () => {
    try {
        const raw = sessionStorage.getItem(KEY);
        if (!raw) return null;
        const p = JSON.parse(raw);
        // Expire après 30 min pour éviter les vieux retours incohérents
        if (Date.now() - (p.at || 0) > 30 * 60 * 1000) return null;
        return p;
    } catch { return null; }
};

const _clear = () => {
    try { sessionStorage.removeItem(KEY); } catch { /* noop */ }
};

/**
 * Hook : sauve l'état courant et navigue vers une fiche prospect.
 * Usage : const goToProspect = useNavigateToProspect(); goToProspect(id, pipeline);
 */
export function useNavigateToProspect() {
    const navigate = useNavigate();
    return useCallback((id, pipeline) => {
        // Ne pas remplacer un state existant si on est déjà sur une fiche prospect
        if (!window.location.pathname.startsWith('/prospects/')) {
            _save(window.location.pathname, window.location.search, window.scrollY || 0);
        }
        navigate(`/prospects/${id}${pipeline ? `?module=${pipeline}` : ''}`);
    }, [navigate]);
}

/**
 * Hook : retourne handleBack() qui restaure exactement la page + scroll sauvegardés.
 * Fallback vers `defaultPath` si aucun état n'est sauvegardé.
 */
export function useReturnFromProspect(defaultPath = '/prospects') {
    const navigate = useNavigate();
    return useCallback(() => {
        const state = _read();
        if (state && state.path) {
            navigate(state.path);
            // Restaure le scroll après le rendu (2 rAF pour laisser le temps au DOM)
            requestAnimationFrame(() => requestAnimationFrame(() => {
                window.scrollTo({ top: state.scroll || 0, left: 0, behavior: 'auto' });
                _clear();
            }));
        } else {
            navigate(defaultPath);
        }
    }, [navigate, defaultPath]);
}

export { _save as saveReturnState, _clear as clearReturnState, _read as readReturnState };

/**
 * Restauration de scroll robuste : les pages liste (Prospects, Pilotage,
 * Dashboard, Fil…) chargent leur data en async. Un simple scrollTo échoue
 * parce que la hauteur du body n'est pas encore suffisante. On fait donc
 * du polling jusqu'à ce que la hauteur soit atteinte OU 3 s écoulées.
 */
const PENDING_KEY = 'valnexo_pending_return_scroll';

function _restoreScrollWithRetry(targetY) {
    if (!targetY || targetY <= 0) return;
    const started = Date.now();
    const MAX_MS = 3000;
    let done = false;
    const tick = () => {
        if (done) return;
        const maxScroll = (document.documentElement.scrollHeight || 0) - (window.innerHeight || 0);
        if (maxScroll >= targetY - 4) {
            window.scrollTo({ top: targetY, left: 0, behavior: 'auto' });
            // Vérifie que le scroll a bien pris ; sinon 1 tick supplémentaire
            requestAnimationFrame(() => {
                if (Math.abs((window.scrollY || 0) - targetY) < 4 || Date.now() - started > MAX_MS) {
                    done = true;
                } else {
                    setTimeout(tick, 60);
                }
            });
            return;
        }
        if (Date.now() - started > MAX_MS) {
            // Timeout : on tente un dernier scroll au max possible
            window.scrollTo({ top: Math.min(targetY, maxScroll), left: 0, behavior: 'auto' });
            done = true;
            return;
        }
        setTimeout(tick, 80);
    };
    // 1er essai après 2 rAF pour laisser le layout initial se stabiliser
    requestAnimationFrame(() => requestAnimationFrame(tick));
}

export function markPendingScroll(y) {
    try { sessionStorage.setItem(PENDING_KEY, String(y || 0)); } catch { /* noop */ }
}

/**
 * Marque un ID de dossier à mettre en évidence + scroll dessus au retour sur la liste.
 * Prend le pas sur PENDING_KEY (scroll numérique) si les deux sont présents.
 */
const PENDING_FOCUS_KEY = 'valnexo_pending_return_focus_id';
export function markPendingFocusId(id) {
    try {
        if (id) sessionStorage.setItem(PENDING_FOCUS_KEY, String(id));
    } catch { /* noop */ }
}
export function readPendingFocusId() {
    try { return sessionStorage.getItem(PENDING_FOCUS_KEY); } catch { return null; }
}
export function clearPendingFocusId() {
    try { sessionStorage.removeItem(PENDING_FOCUS_KEY); } catch { /* noop */ }
}

function _focusRowById(id) {
    if (!id) return;
    const started = Date.now();
    const MAX_MS = 3500;
    const tick = () => {
        // Cherche dans plusieurs selectors (Prospects.jsx utilise prospect-row-<id>,
        // PilotageDashboard utilise dossier-card-<id>-<pipeline>)
        let el = document.querySelector(`[data-testid="prospect-row-${id}"]`);
        if (!el) el = document.querySelector(`[data-testid^="dossier-card-${id}"]`);
        if (el) {
            const rect = el.getBoundingClientRect();
            const targetTop = rect.top + (window.scrollY || 0) - Math.max(80, (window.innerHeight || 800) * 0.25);
            window.scrollTo({ top: Math.max(0, targetTop), left: 0, behavior: 'auto' });
            el.classList.add('vnx-return-highlight');
            setTimeout(() => el.classList.remove('vnx-return-highlight'), 2200);
            return;
        }
        if (Date.now() - started > MAX_MS) return;
        setTimeout(tick, 100);
    };
    requestAnimationFrame(() => requestAnimationFrame(tick));
}

// =============================================================================
// LISTE DES DOSSIERS ADJACENTS — pour boutons "Prochain / Précédent" sur la fiche prospect
// =============================================================================
const LIST_KEY = 'valnexo_prospect_list_context';

/**
 * À appeler par les écrans "liste" (Prospects, Pilotage bucket, Dashboard widget,
 * Fil, Wall, Tâches…) au moment où l'utilisateur clique sur un dossier.
 * Prend en snapshot l'ordre courant + l'ID cliqué pour permettre la navigation
 * "Précédent / Suivant" depuis la fiche prospect.
 */
export function saveProspectListContext(ids, clickedId, source = {}) {
    try {
        if (!Array.isArray(ids) || ids.length === 0) return;
        sessionStorage.setItem(LIST_KEY, JSON.stringify({
            ids,
            clicked: clickedId,
            source: {
                path: source.path || (window.location.pathname + window.location.search),
                label: source.label || 'Retour à la liste',
                pipeline: source.pipeline || null,
            },
            at: Date.now(),
        }));
    } catch { /* noop */ }
}

export function readProspectListContext() {
    try {
        const raw = sessionStorage.getItem(LIST_KEY);
        if (!raw) return null;
        const p = JSON.parse(raw);
        if (Date.now() - (p.at || 0) > 30 * 60 * 1000) return null;
        if (!Array.isArray(p.ids) || p.ids.length === 0) return null;
        return p;
    } catch { return null; }
}

export function clearProspectListContext() {
    try { sessionStorage.removeItem(LIST_KEY); } catch { /* noop */ }
}

/**
 * Hook global à monter au niveau du layout : sauvegarde en continu la page
 * courante (path + scroll) dans sessionStorage, sauf quand on est sur une
 * fiche prospect. Ainsi, quand l'utilisateur ouvre un dossier depuis n'importe
 * où et revient, il retrouve exactement son point de départ.
 *
 * Restaure aussi le scroll à l'arrivée sur une page si le flag pending existe.
 */
export function useTrackReturnState() {
    const location = useLocation();
    const lastPathRef = useRef('');

    useEffect(() => {
        const path = location.pathname;

        // 1) À chaque changement de route, si on arrive sur une page "liste" avec un pending focus/scroll → restaure
        if (!path.startsWith('/prospects/') || path === '/prospects') {
            let focusId = null;
            let pending = 0;
            try {
                focusId = sessionStorage.getItem(PENDING_FOCUS_KEY);
                const raw = sessionStorage.getItem(PENDING_KEY);
                pending = raw ? parseInt(raw, 10) : 0;
            } catch { /* noop */ }
            if (focusId) {
                try { sessionStorage.removeItem(PENDING_FOCUS_KEY); sessionStorage.removeItem(PENDING_KEY); } catch { /* noop */ }
                _focusRowById(focusId);
            } else if (pending > 0) {
                try { sessionStorage.removeItem(PENDING_KEY); } catch { /* noop */ }
                _restoreScrollWithRetry(pending);
            }
        }

        // 2) Ne tracke pas les fiches prospect
        if (path.startsWith('/prospects/') && path !== '/prospects') return;

        lastPathRef.current = path;
        _save(location.pathname, location.search, window.scrollY || 0);

        const onScroll = () => {
            if (window.location.pathname !== lastPathRef.current) return;
            _save(window.location.pathname, window.location.search, window.scrollY || 0);
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [location]);
}
